"use client";

import { MinifiedSideMenu } from "@/components";
import { DevRevProvider } from "@/components/DevRevProvider";
import { OnboardingHeader } from "@/components/Onboarding/components/OnboardingHeader";
import { WeeklyRewardsProvider } from "@/components/WeeklyRewards/WeeklyRewardsProvider";
import { LeftNav } from "@/components/layout/nav/LeftNav";
import { GradientBorder } from "@/components/ui/GradientBorder";
import { BiomeProvider } from "@/context/BiomeProvider";
import { LoginModalProvider } from "@/context/LoginModalContext";
import { OnboardingStepProvider } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { ConnectionProvider } from "@/hooks/connection";
import { Box, Stack } from "@biom3/react";
import Image from "next/image";
import {
  type PropsWithChildren,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

const MAX_WIDTH = "470px";
const SMALL_MAX_WIDTH = "430px";

export default function OnboardingPageLayout({ children }: PropsWithChildren) {
  const { isLoggedIn: isWalletLoggedIn } = usePassportProvider();
  const isLoggedOut =
    isWalletLoggedIn === undefined || isWalletLoggedIn === false;

  const [menuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleMenuClick = useCallback(
    () => setMenuVisible(!menuVisible),
    [menuVisible],
  );

  const sideMenuSx = useMemo(
    () => ({
      translate: {
        default: menuVisible ? "0 0" : "-100% 0",
        medium: "0 0",
      },
      opacity: {
        default: menuVisible ? "1" : "0",
        medium: "1",
      },
      pointerEvents: {
        default: menuVisible ? "auto" : "none",
        medium: "auto",
      },
    }),
    [menuVisible],
  );

  return (
    <Suspense>
      <BiomeProvider>
        <ConnectionProvider>
          <WeeklyRewardsProvider>
            <LoginModalProvider>
              <DevRevProvider>
                <OnboardingStepProvider>
                  {/* Desktop/Tablet: Sidebar + centered modal (≥768px) */}
                  <div className="hidden md:flex h-screen w-screen overflow-hidden">
                    <LeftNav />

                    {/* Desktop/Tablet: Centered modal-like container with background */}
                    <div className="flex-1 overflow-y-auto flex items-center justify-center p-6 relative">
                      {/* Background image */}
                      <Image
                        src="/images/onboarding/onboarding-background.webp"
                        alt="Onboarding background"
                        fill
                        className="object-cover object-center"
                        priority
                      />

                      {/* Modal container with gradient border */}
                      <div className="h-screen w-screen rounded-2xl shadow-2xl shadow-black max-h-[50rem] max-w-md overflow-hidden relative z-10">
                        {isLoggedOut && <GradientBorder />}
                        {children}
                      </div>
                    </div>
                  </div>

                  {/* Mobile only: Original layout (<768px) */}
                  <Stack
                    className="md:hidden"
                    sx={{
                      display: { default: "flex", medium: "none" },
                      pt: { default: "64px", medium: "0" },
                      pos: "relative",
                      h: "100%",
                      overflow: "hidden",
                    }}
                    testId="OnboardingLayout"
                  >
                    <OnboardingHeader
                      isLoggedOut={isLoggedOut}
                      onMenuClick={handleMenuClick}
                    />
                    {menuVisible ? (
                      <Box
                        sx={{
                          display: { default: "flex", medium: "none" },
                        }}
                      >
                        <MinifiedSideMenu
                          domRef={menuRef}
                          onItemClick={handleMenuClick}
                          sx={sideMenuSx}
                        />
                      </Box>
                    ) : null}
                    {children}
                  </Stack>
                </OnboardingStepProvider>
              </DevRevProvider>
            </LoginModalProvider>
          </WeeklyRewardsProvider>
        </ConnectionProvider>
      </BiomeProvider>
    </Suspense>
  );
}
